import React, { useEffect, useRef, useState } from "react";
import { isSmartPhone } from "../utils/utils";

/**
 * アイコンのプロパティ
 */
interface IconProps {
  size?: number;
}

/**
 * フルスクリーンアイコン
 */
const FullscreenIcon: React.FC<IconProps> = ({ size = 25 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    className="transition-transform duration-200 ease-in-out"
    stroke="#020202"
  >
    <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z" />
  </svg>
);

/**
 * フルスクリーン終了アイコン
 */
const FullscreenExitIcon: React.FC<IconProps> = ({ size = 25 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    className="transition-transform duration-200 ease-in-out"
    stroke="#020202"
  >
    <path d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z" />
  </svg>
);

/**
 * 閉じるアイコン
 */
const CloseIcon: React.FC<IconProps> = ({ size = 25 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    className="transition-transform duration-200 ease-in-out"
  >
    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
  </svg>
);

/**
 * フルスクリーンアイコン
 */
const SubMenuIcon: React.FC<IconProps> = ({ size = 25 }) => (
  <svg height={size} viewBox="0 -960 960 960" width={size}  fill="currentColor" >
    <path d="M240-400q-33 0-56.5-23.5T160-480q0-33 23.5-56.5T240-560q33 0 56.5 23.5T320-480q0 33-23.5 56.5T240-400Zm240 0q-33 0-56.5-23.5T400-480q0-33 23.5-56.5T480-560q33 0 56.5 23.5T560-480q0 33-23.5 56.5T480-400Zm240 0q-33 0-56.5-23.5T640-480q0-33 23.5-56.5T720-560q33 0 56.5 23.5T800-480q0 33-23.5 56.5T720-400Z" />
  </svg>
);

const ResetMenuIcon: React.FC<IconProps> = ({ size = 25 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    height={size}
    viewBox="0 -960 960 960"
    width={size}
    fill="#000000"
    stroke="#020202"
  >
    <path d="M480-160q-134 0-227-93t-93-227q0-134 93-227t227-93q69 0 132 28.5T720-690v-110h80v280H520v-80h168q-32-56-87.5-88T480-720q-100 0-170 70t-70 170q0 100 70 170t170 70q77 0 139-44t87-116h84q-28 106-114 173t-196 67Z" />
  </svg>
);
/**
 * チャットヘッダーのプロパティ
 */
interface ChatHeaderProps {
  isMaximized: boolean;
  onToggleMaximize: () => void;
  onChatClose: () => void;
  onSessionReset: () => void;
  title?: string;
}

/**
 * チャットヘッダー
 * タイトル表示、最大化、閉じる
 */
const ChatHeader: React.FC<ChatHeaderProps> = ({
  isMaximized,
  onToggleMaximize,
  onChatClose,
  onSessionReset,
  title = "AIアシスタント パスビーさん",
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const toggleMenu = () => setMenuOpen((open) => !open);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const onDocMouseDown = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => {
      document.removeEventListener("mousedown", onDocMouseDown);
    };
  }, [menuOpen]);

  return (
    <div
      className={`pathbee-chat-header flex items-center px-4 rounded-none min-h-[60px] w-full`}
      style={{
        backgroundColor: "var(--header-bg)",
        color: "var(--header-text)",
        fontSize: "var(--header-font-size)",
      }}
    >
      <div className="flex-1 text-center tracking-wider whitespace-nowrap overflow-hidden text-ellipsis">
        {title}
      </div>

      <div className="flex justify-end gap-3 w-20">
        <button
          aria-label="Open sub menu"
          aria-expanded={menuOpen}
          aria-controls="submenu"
          onClick={toggleMenu}
          className="bg-transparent p-1 rounded-md cursor-pointer"
          style={{ color: "var(--header-text)" }}
        >
          <SubMenuIcon />
        </button>
        {menuOpen && (
          <div
            id="submenu"
            role="menu"
            aria-label="Sub menu"
            style={{gap: "0.1em"}}
            className="absolute right-4 top-15 bg-[#ffffff] border border-[#dddddd] rounded-md shadow-lg flex flex-col shrink-0 w-fit max-w-[196px] z-20"
            ref={containerRef}
          >
            {!isSmartPhone() && (
              <button
                onClick={() => {
                  setMenuOpen(false);
                  onToggleMaximize();
                }}
                aria-label={
                  isMaximized ? "Exit Fullscreen" : "Enter Fullscreen"
                }
                className={`inline-flex items-center gap-2 leading-none w-full text-left px-2 py-2 hover:bg-[#f0f0f0] 
                  transition-all duration-300 ease-in-out active:scale-95 rounded-none cursor-pointer `}
                style={{ color: "#020202", borderRadius: 0, }}
                onMouseDown={(e) => e.stopPropagation()}
              >
                {isMaximized ? <FullscreenExitIcon /> : <FullscreenIcon />}
                <span className="leading-none translate-y-[-1px]">
                  {isMaximized ? "画面を縮小" : "画面を拡大"}
                </span>
              </button>
            )}

            <button
              role="menuitem"
              onClick={() => {
                setMenuOpen(false);
                onSessionReset();
              }}
              onMouseDown={(e) => e.stopPropagation()}
              className={`inline-flex items-center gap-2 leading-none w-full text-left px-2 py-2 transition-all 
                duration-300 ease-in-out hover:bg-[#f0f0f0] active:scale-95 rounded-none cursor-pointer `}
              style={{ color: "#020202", borderRadius: 0 }}
            >
              <ResetMenuIcon />
              <span className="leading-none translate-y-[-1px]">
                会話をリセット
              </span>
            </button>
          </div>
        )}

        <button
          onClick={onChatClose}
          aria-label="閉じる"
          className="p-1 transition-all duration-300 ease-in-out hover:opacity-70 hover:scale-110 active:scale-95 rounded-full cursor-pointer"
          style={{ color: "var(--header-text)" }}
        >
          <CloseIcon />
        </button>
      </div>
    </div>
  );
};

export default ChatHeader;
